import React, { Fragment, useState, useEffect } from 'react';
import { Input } from 'reactstrap';
import { CommonModal, ButtonBuilder, ToastBuilder, ApiRequestBuilderV2 } from '../../../../../../../../GlobalComponents/Builders';
import { parseJSONSafe, handleTabOnKeyDown } from '../../../../../../../../GlobalComponents/Helpers';

/**
 * CopySyncSettingsModal — Admin tool to seed a two-way-sync job's backbone from
 * another, already-built two-way-sync job that moves data between the same pair
 * of services.
 *
 * Flow:
 *   1. Pick a source job (list = other two-way-syncs sharing this service pair).
 *   2. Load a preview. The server auto-FLIPS direction when the source job runs
 *      the opposite way (resources re-attach to the matching-service connection
 *      on THIS job; mappings swap source<->destination).
 *   3. Review/toggle per layer and per row; edit the direction-specific specifics
 *      (resource_options, mapping_filter, after_create_scripts) inline — these are
 *      a starting point, meant to be fine-tuned on the normal job page afterward.
 *   4. Confirm — writes selected resources + resource mappings (INSERT IGNORE).
 *
 * Explicitly EXCLUDED: key & value mappings. Those are client-specific manual
 * effort and have their own dedicated copy feature.
 *
 * All calls go through the existing DMT action route (no new API Gateway paths,
 * no apigClient regeneration — only the api-action-handler Lambda is redeployed):
 *   POST /dmt/jobs/{jobId}/actions/copy-sync-sources    { formData: {} }
 *   POST /dmt/jobs/{jobId}/actions/copy-sync-preview     { formData: { sourceJobId } }
 *   POST /dmt/jobs/{jobId}/actions/copy-sync-settings    { formData: { resources, mappings } }
 */
const CopySyncSettingsModal = ({ jobData, onComplete, autoOpen = false, preselectSourceId = null, onAutoOpenHandled = null }) => {

    const jobId = jobData?.id || jobData?.job_id;

    // Resolve this job's source connection to a dict (it may arrive stringified),
    // so we can compare its service against each candidate's to flag direction.
    const currentSourceConn = (() => {
        const c = jobData?.source_connection;
        if (!c) return {};
        if (typeof c === 'string') { try { return JSON.parse(c); } catch (e) { return {}; } }
        return c;
    })();
    const currentSourceServiceCode = currentSourceConn.service_code || null;

    // A candidate runs the SAME direction as this job when its source service
    // matches ours. The source list is already filtered to this exact service
    // pair, so matching the source side alone is unambiguous.
    const isSameDirection = (s) =>
        currentSourceServiceCode != null &&
        s.source_service_code != null &&
        String(s.source_service_code) === String(currentSourceServiceCode);

    const [modal, setModal] = useState(false);
    const [loadingSources, setLoadingSources] = useState(false);
    const [sources, setSources] = useState([]);
    const [selectedSourceId, setSelectedSourceId] = useState('');

    const [loadingPreview, setLoadingPreview] = useState(false);
    const [preview, setPreview] = useState(null);
    const [resourceRows, setResourceRows] = useState([]);
    const [mappingRows, setMappingRows] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const open = () => {
        setModal(true);
        resetPreview();
        fetchSources();
    };

    const resetPreview = () => {
        setSelectedSourceId('');
        setPreview(null);
        setResourceRows([]);
        setMappingRows([]);
    };

    const fetchSources = () => {
        if (!jobId) return;
        setLoadingSources(true);
        ApiRequestBuilderV2('dmtJobsJobIdActionsActionNamePost', {
            pathParams: { jobId, actionName: 'copy-sync-sources' },
            body: { sourceResourceId: null, formData: {} },
            toastOnError: 'Failed to load source jobs',
        }).then((res) => {
            const raw = res.data?.records ?? res.data;
            const parsed = typeof raw === 'string' ? parseJSONSafe(raw) : raw;
            const rows = parsed?.sources;
            setSources(res.success && Array.isArray(rows) ? rows : []);
            setLoadingSources(false);
        });
    };

    const fetchPreview = (sourceJobId) => {
        if (!sourceJobId) return;
        setLoadingPreview(true);
        ApiRequestBuilderV2('dmtJobsJobIdActionsActionNamePost', {
            pathParams: { jobId, actionName: 'copy-sync-preview' },
            body: { sourceResourceId: null, formData: { sourceJobId } },
            toastOnError: 'Failed to build preview',
        }).then((res) => {
            const raw = res.data?.records ?? res.data;
            const parsed = typeof raw === 'string' ? parseJSONSafe(raw) : raw;

            if (!res.success || !parsed || !parsed.success) {
                ToastBuilder('error', parsed?.message || 'Failed to build preview');
                setPreview(null);
                setResourceRows([]);
                setMappingRows([]);
                setLoadingPreview(false);
                return;
            }

            setPreview(parsed);
            // Coerce a textarea value: objects (JSON columns) become pretty JSON
            // strings; plain strings pass through untouched. Prevents "[object Object]".
            const toText = (v) => {
                if (v === null || v === undefined) return '';
                if (typeof v === 'string') return v;
                try { return JSON.stringify(v, null, 2); } catch (e) { return String(v); }
            };
            // Does this options blob carry job-specific scope IDs we may want to blank?
            const hasScopeIds = (text) => /"(?:parent_resource_ids|setup_ids)"\s*:/.test(text || '');
            // Default selection: everything that does not already exist.
            setResourceRows((parsed.resources || []).map((r) => {
                const optionsText = toText(r.resource_options);
                return {
                    ...r,
                    _selected: !r.already_exists,
                    _options: optionsText,
                    _hasScopeIds: hasScopeIds(optionsText),
                    _resetScopeIds: false,
                };
            }));
            setMappingRows((parsed.mappings || []).map((m) => ({
                ...m,
                _selected: !m.already_exists,
                _filter: toText(m.mapping_filter),
                _afterScripts: toText(m.after_create_scripts),
            })));
            setLoadingPreview(false);
        });
    };

    useEffect(() => {
        if (selectedSourceId) fetchPreview(selectedSourceId);
        else setPreview(null);
    }, [selectedSourceId]);

    // Auto-open support — used right after creating a two-way-sync via the
    // "Seed from another sync" create option (?seedSync=1&seedFrom=<id>).
    useEffect(() => {
        if (autoOpen && jobId) {
            setModal(true);
            resetPreview();
            fetchSources();
            if (preselectSourceId) setSelectedSourceId(String(preselectSourceId));
            onAutoOpenHandled && onAutoOpenHandled();
        }
    }, [autoOpen, jobId]);

    const toggleAllResources = (value) => setResourceRows((rows) => rows.map((r) => ({ ...r, _selected: value })));
    const toggleAllMappings = (value) => setMappingRows((rows) => rows.map((m) => ({ ...m, _selected: value })));

    const updateResource = (idx, patch) => setResourceRows((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const updateMapping = (idx, patch) => setMappingRows((rows) => rows.map((m, i) => (i === idx ? { ...m, ...patch } : m)));

    // Bulk scope-ID input (applies to every resource's setup_ids / parent_resource_ids).
    const [bulkScopeIds, setBulkScopeIds] = useState('');

    // Parse the box into an int array. Empty box -> [] (blank the scope arrays).
    // Accepts comma / whitespace separated IDs; non-numeric tokens are dropped.
    const parseScopeIds = (text) => {
        if (!text || !text.trim()) return [];
        return text.split(/[\s,]+/).map((t) => t.trim()).filter(Boolean)
            .map((t) => (Number.isNaN(Number(t)) ? t : Number(t)));
    };

    // Write the given IDs into whichever scope keys each resource's options
    // already define (parent_resource_ids / setup_ids). Rows without those keys
    // are left untouched; rows with invalid JSON are skipped (fixable later).
    const applyBulkScopeIds = () => {
        const ids = parseScopeIds(bulkScopeIds);
        setResourceRows((rows) => rows.map((r) => {
            if (!r._hasScopeIds) return r;
            try {
                const parsed = JSON.parse(r._options);
                if (!parsed || typeof parsed !== 'object') return r;
                let touched = false;
                ['parent_resource_ids', 'setup_ids'].forEach((k) => {
                    if (k in parsed) { parsed[k] = ids; touched = true; }
                });
                if (!touched) return r;
                return { ...r, _options: JSON.stringify(parsed, null, 2), _resetScopeIds: ids.length === 0 };
            } catch (err) {
                return r; // bad JSON — leave as-is
            }
        }));
    };

    const selectedResourceCount = resourceRows.filter((r) => r._selected).length;
    const selectedMappingCount = mappingRows.filter((m) => m._selected).length;

    const handleSubmit = () => {
        const resources = resourceRows.filter((r) => r._selected).map((r) => ({
            connectionId: r.target_connection_id,
            resourceId: r.resource_id,
            resourceOptions: r._options || null,
            resourceUsage: r.resource_usage || null,
            resetScopeIds: !!r._resetScopeIds,
        }));

        const mappings = mappingRows.filter((m) => m._selected).map((m) => ({
            sourceResourceId: m.source_resource_id,
            destinationResourceId: m.destination_resource_id,
            notes: m.notes || null,
            mappingFilter: m._filter || null,
            afterCreateScripts: m._afterScripts || null,
            mappingType: m.mapping_type || null,
            mappingOptions: m.mapping_options || null,
        }));

        if (resources.length === 0 && mappings.length === 0) {
            return ToastBuilder('error', 'Select at least one resource or mapping to copy');
        }

        ApiRequestBuilderV2('dmtJobsJobIdActionsActionNamePost', {
            pathParams: { jobId, actionName: 'copy-sync-settings' },
            body: { sourceResourceId: null, formData: { resources, mappings } },
            setIsProcessing: setSubmitting,
            toastOnError: true,
        }).then((res) => {
            const raw = res.data?.records ?? res.data;
            const parsed = typeof raw === 'string' ? parseJSONSafe(raw) : raw;
            if (res.success && parsed?.success) {
                ToastBuilder('success', parsed.message || 'Settings copied');
                setModal(false);
                resetPreview();
                onComplete && onComplete();
            } else {
                ToastBuilder('error', parsed?.message || 'Failed to copy settings');
            }
        });
    };

    const rowBoxStyle = {
        padding: '8px 12px', marginBottom: 6,
        borderLeft: '3px solid rgba(128,128,128,0.2)',
        background: 'rgba(128,128,128,0.02)', borderRadius: 2,
    };

    return (
        <Fragment>
            <ButtonBuilder label='Setting Import' className='btn-outline-warning'
                useSpinner={false} onClick={open} />

            <CommonModal isOpen={modal} title='Copy Sync Settings' size='xl'
                toggler={() => { setModal(false); resetPreview(); }}
                onSubmit={!!preview}
                onSubmitLabel={`Copy ${selectedResourceCount} resource(s) + ${selectedMappingCount} mapping(s)`}
                customSubmit={handleSubmit}
                onCancelLabel='Close'>

                <p style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
                    Seed this job's backbone from another two-way-sync between the same services.
                    Direction is flipped automatically when the source runs the opposite way.
                    Resource options and mapping specifics are copied as a <strong>starting point</strong> —
                    review and fine-tune them here or on the job page. Key &amp; value mappings are
                    not copied (client-specific, built manually).
                </p>

                <div style={{ marginBottom: 16 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6 }}>Copy from</span>
                    {loadingSources ? (
                        <p style={{ color: '#999', fontSize: 13 }}>Loading source jobs...</p>
                    ) : sources.length === 0 ? (
                        <p style={{ color: '#999', fontSize: 13 }}>No comparable two-way-sync jobs found for this service pair.</p>
                    ) : (
                        <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid rgba(128,128,128,0.2)', borderRadius: 3 }}>
                            {sources.map((s) => {
                                const same = isSameDirection(s);
                                const selected = String(selectedSourceId) === String(s.job_id);
                                return (
                                    <div key={s.job_id}
                                        onClick={() => setSelectedSourceId(String(s.job_id))}
                                        style={{
                                            padding: '7px 12px', cursor: 'pointer', fontSize: 13,
                                            borderLeft: `3px solid ${selected ? 'var(--theme-default)' : 'transparent'}`,
                                            background: selected ? 'rgba(36,105,92,0.08)' : 'transparent',
                                            borderBottom: '1px solid rgba(128,128,128,0.1)',
                                        }}>
                                        <span title={selected ? 'Selected' : 'Not selected'}
                                            style={{ fontSize: '1.1em', marginRight: 6 }}>
                                            {selected ? '◆' : '◇'}
                                        </span>
                                        <span style={{ fontWeight: 600 }}>{s.job_id} — {s.partner_name} — {s.name}</span>
                                        <span style={{ color: '#999', fontSize: 11, marginLeft: 8 }}>
                                            {s.source_service_label} → {s.destination_service_label} · {s.resource_count} res / {s.mapping_count} map
                                        </span>
                                        {same ? (
                                            <span className='txt-primary' style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, marginLeft: 8 }}
                                                title='Same direction as this job'>
                                                SERVICE PARITY
                                            </span>
                                        ) : (
                                            <span className='txt-secondary' style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, marginLeft: 8 }}
                                                title='Opposite direction — resources and mappings will be flipped'>
                                                FLIPPED
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {loadingPreview && <p style={{ textAlign: 'center', color: '#999' }}>Building preview...</p>}

                {preview && !loadingPreview && (
                    <Fragment>
                        <div style={{
                            fontSize: 12, marginBottom: 14, padding: '6px 10px', borderRadius: 3,
                            background: preview.flip ? 'rgba(186,137,93,0.12)' : 'rgba(36,105,92,0.1)',
                        }}>
                            {preview.flip
                                ? 'Opposite direction detected — resources and mappings are flipped to match this job.'
                                : 'Same direction — copied as-is (no flip).'}
                        </div>

                        {/* ---- Bulk scope IDs ---- */}
                        {resourceRows.some((r) => r._hasScopeIds) && (
                            <div style={{ marginBottom: 14, padding: '8px 12px', borderRadius: 3, background: 'rgba(128,128,128,0.04)', border: '1px solid rgba(128,128,128,0.15)' }}>
                                <span style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Scope IDs</span>
                                <p style={{ fontSize: 11, color: '#888', margin: '0 0 6px' }}>
                                    Applies to every resource's <code>setup_ids</code> / <code>parent_resource_ids</code>.
                                    Leave blank to set them to <code>[]</code>. You can still edit each resource below after applying.
                                </p>
                                <div className='d-flex align-items-center' style={{ gap: 8 }}>
                                    <Input bsSize='sm' value={bulkScopeIds}
                                        onChange={(e) => setBulkScopeIds(e.target.value)}
                                        placeholder='e.g. 198, 199, 200, 332 (blank = [])'
                                        style={{ fontSize: 12, fontFamily: 'monospace', maxWidth: 320 }} />
                                    <ButtonBuilder label='Apply to all' className='btn-outline-secondary btn-sm' color={null}
                                        onClick={applyBulkScopeIds} useSpinner={false} />
                                </div>
                            </div>
                        )}

                        {/* ---- Resources ---- */}
                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                                Resources ({selectedResourceCount}/{resourceRows.length})
                            </span>
                            <div className='d-flex gap-2'>
                                <ButtonBuilder label='All' className='btn-outline-secondary btn-sm' color={null}
                                    onClick={() => toggleAllResources(true)} useSpinner={false} />
                                <ButtonBuilder label='None' className='btn-outline-secondary btn-sm' color={null}
                                    onClick={() => toggleAllResources(false)} useSpinner={false} />
                            </div>
                        </div>
                        {resourceRows.length === 0 ? (
                            <p style={{ color: '#999', fontSize: 12 }}>No resources on the source job.</p>
                        ) : (
                            <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 18, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, alignItems: 'start' }}>
                                {resourceRows.map((r, idx) => (
                                    <div key={`${r.target_connection_id}-${r.resource_id}`} style={rowBoxStyle}>
                                        <div className='d-flex align-items-center' style={{ gap: 10 }}>
                                            <Input type='checkbox' checked={r._selected}
                                                onChange={(e) => updateResource(idx, { _selected: e.target.checked })}
                                                style={{ marginTop: 0 }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <span style={{ fontSize: 13, fontWeight: 600 }}>{r.resource_name}</span>
                                                <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>
                                                    {r.service_label} · id {r.resource_id}
                                                    {r.resource_usage ? ` · ${r.resource_usage}` : ''}
                                                </span>
                                                {r.already_exists && (
                                                    <span style={{ fontSize: 10, color: '#ba895d', marginLeft: 8 }}>already on job</span>
                                                )}
                                            </div>
                                        </div>
                                        {r._selected && (
                                            <Fragment>
                                                {r._hasScopeIds && (
                                                    <label className='d-flex align-items-center'
                                                        style={{ gap: 6, margin: '6px 0 0', cursor: 'pointer' }}
                                                        title='Blank out parent_resource_ids / setup_ids — these are job-specific and should not carry over'>
                                                        <Input type='checkbox' checked={!!r._resetScopeIds}
                                                            onChange={(e) => {
                                                                const reset = e.target.checked;
                                                                const patch = { _resetScopeIds: reset };
                                                                // Live-preview the blanked arrays in the textarea.
                                                                try {
                                                                    const parsed = JSON.parse(r._options);
                                                                    if (parsed && typeof parsed === 'object') {
                                                                        ['parent_resource_ids', 'setup_ids'].forEach((k) => {
                                                                            if (k in parsed) parsed[k] = reset ? [] : parsed[k];
                                                                        });
                                                                        if (reset) patch._options = JSON.stringify(parsed, null, 2);
                                                                    }
                                                                } catch (err) { /* bad JSON — leave text, backend handles */ }
                                                                updateResource(idx, patch);
                                                            }}
                                                            style={{ marginTop: 0 }} />
                                                        <span style={{ fontSize: 11 }} className='txt-secondary'>
                                                            Reset scope IDs to <code>[]</code> (parent_resource_ids / setup_ids)
                                                        </span>
                                                    </label>
                                                )}
                                                <Input type='textarea' bsSize='sm' value={r._options}
                                                    onChange={(e) => updateResource(idx, { _options: e.target.value })}
                                                    onKeyDown={handleTabOnKeyDown}
                                                    placeholder='resource_options (JSON) — optional'
                                                    style={{ marginTop: 6, fontSize: 11, fontFamily: 'monospace' }} rows={2} />
                                            </Fragment>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* ---- Resource Mappings ---- */}
                        <div className='d-flex justify-content-between align-items-center' style={{ marginBottom: 6 }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                                Resource Mappings ({selectedMappingCount}/{mappingRows.length})
                            </span>
                            <div className='d-flex gap-2'>
                                <ButtonBuilder label='All' className='btn-outline-secondary btn-sm' color={null}
                                    onClick={() => toggleAllMappings(true)} useSpinner={false} />
                                <ButtonBuilder label='None' className='btn-outline-secondary btn-sm' color={null}
                                    onClick={() => toggleAllMappings(false)} useSpinner={false} />
                            </div>
                        </div>
                        {mappingRows.length === 0 ? (
                            <p style={{ color: '#999', fontSize: 12 }}>No resource mappings on the source job.</p>
                        ) : (
                            <div style={{ maxHeight: 300, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, alignItems: 'start' }}>
                                {mappingRows.map((m, idx) => (
                                    <div key={`${m.source_resource_id}-${m.destination_resource_id}`} style={rowBoxStyle}>
                                        <div className='d-flex align-items-center' style={{ gap: 10 }}>
                                            <Input type='checkbox' checked={m._selected}
                                                onChange={(e) => updateMapping(idx, { _selected: e.target.checked })}
                                                style={{ marginTop: 0 }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <span style={{ fontSize: 13, fontWeight: 600 }}>
                                                    {m.source_resource_name} → {m.destination_resource_name}
                                                </span>
                                                <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>
                                                    {m.source_resource_id}-{m.destination_resource_id}
                                                    {m.mapping_type ? ` · ${m.mapping_type}` : ''}
                                                </span>
                                                {m.already_exists && (
                                                    <span style={{ fontSize: 10, color: '#ba895d', marginLeft: 8 }}>already on job</span>
                                                )}
                                            </div>
                                        </div>
                                        {m._selected && (
                                            <div style={{ marginTop: 6 }}>
                                                <Input type='textarea' bsSize='sm' value={m._filter}
                                                    onChange={(e) => updateMapping(idx, { _filter: e.target.value })}
                                                    onKeyDown={handleTabOnKeyDown}
                                                    placeholder='mapping_filter — direction-specific, edit as needed'
                                                    style={{ fontSize: 11, fontFamily: 'monospace' }} rows={2} />
                                                <Input type='textarea' bsSize='sm' value={m._afterScripts}
                                                    onChange={(e) => updateMapping(idx, { _afterScripts: e.target.value })}
                                                    onKeyDown={handleTabOnKeyDown}
                                                    placeholder='after_create_scripts — direction-specific, edit as needed'
                                                    style={{ marginTop: 4, fontSize: 11, fontFamily: 'monospace' }} rows={2} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Fragment>
                )}
            </CommonModal>
        </Fragment>
    );
};

export default CopySyncSettingsModal;
